import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import IceProfileEditor from "@/components/IceProfileEditor";
import IceSharesPanel from "@/components/IceSharesPanel";
import {
  Printer,
  AlertTriangle,
  Scroll,
  Users,
  Landmark,
  Share2,
  Bitcoin,
  Mail,
  Smartphone,
  Shield,
  Lock,
  FileText,
  Pencil,
  Eye,
  User,
  Heart,
  Scale,
  Baby,
  HardHat,
  Stethoscope,
  TrendingUp,
  Gem,
  KeyRound,
  Flower,
  PencilLine,
  Send,
  Copy,
  Check,
  X,
} from "lucide-react";

const CAT_META = {
  bank:      { icon: Landmark,   key_label: "ice_cat_bank" },
  social:    { icon: Share2,     key_label: "ice_cat_social" },
  crypto:    { icon: Bitcoin,    key_label: "ice_cat_crypto" },
  email:     { icon: Mail,       key_label: "ice_cat_email" },
  device:    { icon: Smartphone, key_label: "ice_cat_device" },
  insurance: { icon: Shield,     key_label: "ice_cat_insurance" },
};

function Section({ id, icon: Icon, title, children, testid }) {
  return (
    <section
      id={id}
      className="glass-card rounded-md p-5 sm:p-6 break-inside-avoid"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gold-subtle">
        {Icon && <Icon className="w-5 h-5 text-gold" />}
        <h2 className="font-display text-xl text-gold-light">{title}</h2>
      </div>
      <div className="space-y-2 text-sm">{children}</div>
    </section>
  );
}

function KV({ label, value, mono = false }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="grid grid-cols-3 gap-3 py-1">
      <div className="text-faded font-label col-span-1">{label}</div>
      <div className={`text-gold-light col-span-2 break-words ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </div>
    </div>
  );
}

/* True iff the value carries any actual user data. */
function _has(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.some(_has);
  if (typeof v === "object") return Object.values(v).some(_has);
  return Boolean(v);
}

/* Renders a bullet-list of free-form rows like {name, contact} or {description, location}.
   Only renders if at least one row carries data. Returns null otherwise so the
   parent <Section> can be hidden via _has(). */
function RowList({ rows, fields }) {
  const visible = (rows || []).filter((r) => fields.some((f) => _has(r?.[f.key])));
  if (visible.length === 0) return null;
  return (
    <ul className="space-y-3">
      {visible.map((row, i) => (
        <li key={i} className="border-l-2 border-gold/40 pl-3 text-sm">
          {fields.map((f) =>
            _has(row[f.key]) ? (
              <div key={f.key} className="leading-relaxed">
                <span className="font-label text-faded text-[11px]">{f.label}: </span>
                <span className="text-gold-light whitespace-pre-wrap">{row[f.key]}</span>
              </div>
            ) : null
          )}
        </li>
      ))}
    </ul>
  );
}

export default function IceDocument() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heirs, setHeirs] = useState([]);
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("ks_ice_mode") || "summary"; } catch { return "summary"; }
  });
  const [shareOpen, setShareOpen] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [sharesRefresh, setSharesRefresh] = useState(0);

  const setModePersisted = (m) => {
    setMode(m);
    try { localStorage.setItem("ks_ice_mode", m); } catch { /* ignore quota */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get("/ice-summary"),
        api.get("/heirs").catch(() => ({ data: [] })),
      ]);
      setData(s.data);
      setHeirs(h.data || []);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const print = () => window.print();

  const handleShare = async () => {
    setSharing(true);
    setShareData(null);
    try {
      const r = await api.post("/ice/share");
      setShareData(r.data);
      setShareOpen(true);
      setCodeCopied(false);
      setSharesRefresh((n) => n + 1);  // trigger IceSharesPanel reload
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      const msg =
        code === "no_executor_email"
          ? t("ice_share_err_no_executor")
          : err?.response?.status === 403
          ? null /* paywall handled globally */
          : t("ice_share_err_generic");
      if (msg) {
        // dynamic import sonner to avoid blow-up if not loaded
        const { toast } = await import("sonner");
        toast.error(msg);
      }
    } finally {
      setSharing(false);
    }
  };

  const copyCode = async () => {
    if (!shareData?.code) return;
    try {
      await navigator.clipboard.writeText(shareData.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1800);
    } catch { /* clipboard may be blocked */ }
  };

  if (loading) {
    return <div className="text-faded" data-testid="ice-loading">{t("loading") || "…"}</div>;
  }

  if (!data) {
    return (
      <div className="text-muted" data-testid="ice-empty">
        {t("ice_empty")}
      </div>
    );
  }

  const willExists = data.will?.has_will === "yes";
  const willMissing = data.will?.has_will === "no";

  // Progress — counts populated profile sections + will + executor + heirs
  // out of a fixed total to give the user a "completion" feel.
  const totalSections = 20;
  const completed = (() => {
    let n = 0;
    if (data.owner?.name) n++;
    if (_has(data.will?.has_will) && data.will.has_will !== "unknown") n++;
    if (_has(data.will?.executor_name)) n++;
    if (_has(data.executor_instructions)) n++;
    if (_has(data.physical_documents)) n++;
    if ((data.heirs || []).length > 0) n++;
    const p = data.profile || {};
    if (_has(p.personal)) n++;
    if (_has(p.marital)) n++;
    if (_has(p.divorce)) n++;
    if (_has(p.children)) n++;
    if (_has(p.guardians)) n++;
    if (_has(p.domestic_staff)) n++;
    if (_has(p.pro_contacts)) n++;
    if (_has(p.medical)) n++;
    if (_has(p.bank_accounts)) n++;
    if (_has(p.investments)) n++;
    if (_has(p.valuables)) n++;
    if (_has(p.credentials_location)) n++;
    if (_has(p.social_accounts)) n++;
    if (_has(p.funeral_wishes) || _has(p.additional_notes)) n++;
    return n;
  })();
  const completedPct = Math.round((completed / totalSections) * 100);

  const fmtDate = (() => {
    try {
      return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : lang === "zh" ? "zh-CN" : lang === "ko" ? "ko-KR" : "en-US", {
        dateStyle: "long",
      }).format(new Date(data.generated_at));
    } catch {
      return data.generated_at;
    }
  })();

  return (
    <div data-testid="ice-page" className="ice-doc">
      <PageHeader
        title={t("ice_title")}
        subtitle={t("ice_subtitle")}
        action={
          <div
            className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-end gap-2 w-full sm:w-auto print:hidden"
            data-testid="ice-header-actions"
          >
            {mode === "summary" && (
              <button
                type="button"
                onClick={handleShare}
                disabled={sharing}
                className="btn-ghost w-full sm:w-auto justify-center whitespace-nowrap"
                data-testid="ice-share-btn"
              >
                <Send className="w-4 h-4" />
                {sharing ? (t("saving") || "…") : t("ice_share_btn")}
              </button>
            )}
            {mode === "summary" && (
              <button
                type="button"
                onClick={print}
                className="btn-gold w-full sm:w-auto justify-center whitespace-nowrap order-first sm:order-last"
                data-testid="ice-print-btn"
              >
                <Printer className="w-4 h-4" />
                <span className="sm:hidden">{t("ice_print_short")}</span>
                <span className="hidden sm:inline">{t("ice_print")}</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setModePersisted("edit")}
              aria-pressed={mode === "edit"}
              className={`btn-ghost w-full sm:w-auto justify-center whitespace-nowrap ${
                mode === "edit" ? "ks-toggle-active" : ""
              }`}
              data-testid="ice-mode-edit"
            >
              <Pencil className="w-4 h-4" /> {t("ice_mode_edit")}
            </button>
            <button
              type="button"
              onClick={() => setModePersisted("summary")}
              aria-pressed={mode === "summary"}
              className={`btn-ghost w-full sm:w-auto justify-center whitespace-nowrap ${
                mode === "summary" ? "ks-toggle-active" : ""
              }`}
              data-testid="ice-mode-summary"
            >
              <Eye className="w-4 h-4" /> {t("ice_mode_summary")}
            </button>
          </div>
        }
      />

      {/* Fixed legal disclaimer — always visible at the top */}
      <div className="bg-amber-500/5 border border-amber-500/40 rounded-md p-4 mb-6 flex gap-3 text-xs text-amber-100/90"
        data-testid="ice-legal-disclaimer">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
        <span>{t("ice_legal_notice")}</span>
      </div>

      {mode === "edit" ? (
        <IceProfileEditor heirs={heirs} onSaved={load} />
      ) : (
        <>
      {/* Completion progress — only in summary mode */}
      <div className="glass-card rounded-md p-4 mb-4 print:hidden" data-testid="ice-progress">
        <div className="flex items-baseline justify-between mb-2">
          <span className="font-label text-faded text-xs">{t("ice_progress_label")}</span>
          <span
            className="text-gold-light font-display text-sm"
            data-testid="ice-progress-count"
          >
            {completed} / {totalSections} · {completedPct}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden border border-gold-subtle">
          <div
            className="h-full bg-gradient-to-r from-gold/80 to-gold-light/80 transition-all duration-500"
            style={{ width: `${completedPct}%` }}
            data-testid="ice-progress-bar"
          />
        </div>
        <p className="text-[11px] text-faded mt-2">
          {completedPct < 100 ? t("ice_progress_hint") : t("ice_progress_done")}
        </p>
      </div>

      {/* Disclaimer banner (hidden in print) */}
      <div className="bg-red-500/5 border border-red-500/30 rounded-md p-4 mb-6 flex gap-3 text-xs text-red-200/90 print:hidden">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>{t("ice_warning")}</span>
      </div>

      {/* Active share links — owner-only audit panel */}
      <div className="mb-6">
        <IceSharesPanel refreshSignal={sharesRefresh} />
      </div>

      <div className="space-y-6">
        {/* Cover */}
        <Section icon={Scroll} title={t("ice_cover_title")} testid="ice-cover">
          <KV label={t("ice_owner_name")} value={data.owner?.name} />
          <KV label={t("ice_owner_email")} value={data.owner?.email} />
          <KV label={t("ice_generated_at")} value={fmtDate} />
        </Section>

        {/* Personal data */}
        {_has(data.profile?.personal) && (
          <Section icon={User} title={t("ice_sec_personal")} testid="ice-personal">
            <KV label={t("ice_p_full_name")} value={data.profile.personal.full_name} />
            <KV label={t("ice_p_id_number")} value={data.profile.personal.id_number} />
            <KV label={t("ice_p_phone_home")} value={data.profile.personal.phone_home} />
            <KV label={t("ice_p_phone_work")} value={data.profile.personal.phone_work} />
            <KV label={t("ice_p_passport")} value={data.profile.personal.passport} />
            <KV label={t("ice_p_nationality")} value={data.profile.personal.nationality} />
            <KV label={t("ice_p_residence")} value={data.profile.personal.residence} />
            <KV label={t("ice_p_email")} value={data.profile.personal.email} />
          </Section>
        )}

        {/* Marital & spouse */}
        {_has(data.profile?.marital) && (
          <Section icon={Heart} title={t("ice_sec_marital")} testid="ice-marital">
            <KV label={t("ice_m_status")} value={data.profile.marital.status} />
            <KV label={t("ice_m_spouse_name")} value={data.profile.marital.spouse_name} />
            <KV label={t("ice_m_spouse_id")} value={data.profile.marital.spouse_id} />
            <KV label={t("ice_m_spouse_phone")} value={data.profile.marital.spouse_phone} />
            <KV label={t("ice_m_spouse_email")} value={data.profile.marital.spouse_email} />
          </Section>
        )}

        {/* Previous divorce */}
        {_has(data.profile?.divorce) && (
          <Section icon={Scale} title={t("ice_sec_divorce")} testid="ice-divorce">
            <KV label={t("ice_d_agreement_location")} value={data.profile.divorce.agreement_location} />
            <KV label={t("ice_d_notes")} value={data.profile.divorce.notes} />
          </Section>
        )}

        {/* Will & Executor */}
        <Section icon={Scroll} title={t("ice_will_title")} testid="ice-will">
          {willExists && (
            <>
              <KV label={t("will_location_label")} value={data.will?.will_location} />
            </>
          )}
          {willMissing && (
            <p className="text-yellow-500/90 text-sm">{t("ice_will_missing")}</p>
          )}
          {!willExists && !willMissing && (
            <p className="text-faded text-sm">{t("ice_will_unknown")}</p>
          )}
          <div className="border-t border-gold-subtle pt-3 mt-3">
            <div className="font-label text-faded mb-2">{t("will_executor_section")}</div>
            <KV label={t("will_executor_name")} value={data.will?.executor_name} />
            <KV label={t("will_executor_relationship")} value={data.will?.executor_relationship} />
            <KV label={t("will_executor_phone")} value={data.will?.executor_phone} />
            <KV label={t("will_executor_email")} value={data.will?.executor_email} />
            {data.will?.notes && <KV label={t("will_notes")} value={data.will.notes} />}
            {!data.will?.executor_name && (
              <p className="text-faded text-xs mt-2">
                {t("ice_no_executor")}{" "}
                <Link to="/will-executor" className="text-gold underline">
                  {t("ice_add_executor")}
                </Link>
              </p>
            )}
          </div>
        </Section>

        {/* Instructions for executor */}
        {data.executor_instructions ? (
          <Section icon={Scroll} title={t("ice_instructions_title")} testid="ice-instructions">
            <div className="whitespace-pre-wrap text-gold-light">{data.executor_instructions}</div>
          </Section>
        ) : (
          <Section icon={Scroll} title={t("ice_instructions_title")} testid="ice-instructions-empty">
            <p className="text-faded">
              {t("ice_no_instructions")}{" "}
              <Link to="/will-executor" className="text-gold underline">
                {t("ice_add_instructions")}
              </Link>
            </p>
          </Section>
        )}

        {/* Physical documents — DNI, deeds, paper insurance, etc. */}
        <Section icon={FileText} title={t("ice_phys_title")} testid="ice-physical">
          {(!data.physical_documents || data.physical_documents.length === 0) ? (
            <p className="text-faded">
              {t("ice_no_phys")}{" "}
              <Link to="/will-executor" className="text-gold underline">
                {t("ice_add_phys")}
              </Link>
            </p>
          ) : (
            <ul className="space-y-3">
              {data.physical_documents.map((p, i) => (
                <li key={i} className="border-l-2 border-gold/40 pl-3" data-testid={`ice-phys-${i}`}>
                  <div className="text-gold-light font-medium">{p.title || "—"}</div>
                  {p.location && (
                    <div className="text-xs text-muted mt-0.5">
                      <span className="font-label text-faded">{t("ice_phys_loc")}: </span>
                      {p.location}
                    </div>
                  )}
                  {p.notes && (
                    <div className="text-xs text-muted mt-0.5 whitespace-pre-wrap">
                      {p.notes}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Heirs */}
        <Section icon={Users} title={t("ice_heirs_title")} testid="ice-heirs">
          {data.heirs?.length === 0 ? (
            <p className="text-faded">{t("ice_no_heirs")}</p>
          ) : (
            <ul className="space-y-3">
              {data.heirs.map((h) => {
                const ov = data.profile?.heir_overrides?.[h.heir_id] || {};
                const notify = ov.notify !== false;
                return (
                  <li key={h.heir_id} className="border-l-2 border-gold/40 pl-3">
                    <div className="text-gold-light">
                      {h.name}{" "}
                      {h.relationship && (
                        <span className="text-faded text-xs">· {h.relationship}</span>
                      )}
                      {typeof h.share_percent === "number" && h.share_percent > 0 && (
                        <span className="text-gold text-xs ml-2">{h.share_percent}%</span>
                      )}
                      {!notify && (
                        <span className="text-amber-300 text-[10px] uppercase tracking-wider ml-2">
                          {t("ice_h_do_not_notify")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted">
                      {h.email}{h.phone ? ` · ${h.phone}` : ""}
                    </div>
                    {ov.bequest && (
                      <div className="text-xs text-muted mt-1 whitespace-pre-wrap">
                        <span className="font-label text-faded">{t("ice_h_bequest_short")}: </span>
                        {ov.bequest}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* Children */}
        {_has(data.profile?.children) && (
          <Section icon={Baby} title={t("ice_sec_children")} testid="ice-children-summary">
            <RowList
              rows={data.profile.children}
              fields={[
                { key: "name", label: t("ice_c_name") },
                { key: "contact", label: t("ice_c_contact") },
              ]}
            />
          </Section>
        )}

        {/* Guardians */}
        {_has(data.profile?.guardians) && (
          <Section icon={Shield} title={t("ice_sec_guardians")} testid="ice-guardians-summary">
            <RowList
              rows={data.profile.guardians}
              fields={[
                { key: "name", label: t("ice_g_name") },
                { key: "contact", label: t("ice_g_contact") },
              ]}
            />
          </Section>
        )}

        {/* Professional contacts */}
        {_has(data.profile?.pro_contacts) && (
          <Section icon={Stethoscope} title={t("ice_sec_pro_contacts")} testid="ice-pro-summary">
            {[
              { k: "doctor", label: t("ice_pro_doctor") },
              { k: "lawyer", label: t("ice_pro_lawyer") },
              { k: "financial_advisor", label: t("ice_pro_advisor") },
            ].map(({ k, label }) => {
              const c = data.profile.pro_contacts?.[k] || {};
              if (!_has(c)) return null;
              return (
                <div key={k} className="border-l-2 border-gold/40 pl-3 mb-2 text-sm">
                  <div className="text-gold-light font-medium">{label}</div>
                  <KV label={t("ice_pro_name")} value={c.name} />
                  <KV label={t("ice_pro_contact")} value={c.contact} />
                  <KV label={t("ice_pro_email")} value={c.email} />
                </div>
              );
            })}
          </Section>
        )}

        {/* Medical */}
        {_has(data.profile?.medical) && (
          <Section icon={Stethoscope} title={t("ice_sec_medical")} testid="ice-medical-summary">
            <KV label={t("ice_med_insurance")} value={data.profile.medical.insurance} />
            <KV label={t("ice_med_member_id")} value={data.profile.medical.member_id} />
            <KV label={t("ice_med_blood_type")} value={data.profile.medical.blood_type} />
            <KV
              label={t("ice_med_living_will")}
              value={
                data.profile.medical.living_will === "yes"
                  ? t("yes")
                  : data.profile.medical.living_will === "no"
                  ? t("no")
                  : null
              }
            />
          </Section>
        )}

        {/* Domestic staff */}
        {_has(data.profile?.domestic_staff) && (
          <Section icon={HardHat} title={t("ice_sec_staff")} testid="ice-staff-summary">
            <RowList
              rows={data.profile.domestic_staff}
              fields={[
                { key: "name", label: t("ice_s_name") },
                { key: "contact", label: t("ice_s_contact") },
              ]}
            />
          </Section>
        )}

        {/* Bank accounts */}
        {_has(data.profile?.bank_accounts) && (
          <Section icon={Landmark} title={t("ice_sec_banks")} testid="ice-banks-summary">
            <RowList
              rows={data.profile.bank_accounts}
              fields={[
                { key: "entity", label: t("ice_b_entity") },
                { key: "reference", label: t("ice_b_reference") },
                { key: "auto_payments", label: t("ice_b_auto_payments") },
              ]}
            />
            <p className="text-[11px] text-faded italic mt-2">{t("ice_b_no_creds")}</p>
          </Section>
        )}

        {/* Investments */}
        {_has(data.profile?.investments) && (
          <Section icon={TrendingUp} title={t("ice_sec_investments")} testid="ice-investments-summary">
            <RowList
              rows={data.profile.investments}
              fields={[
                { key: "type", label: t("ice_inv_type") },
                { key: "platform", label: t("ice_inv_platform") },
                { key: "reference", label: t("ice_inv_reference") },
              ]}
            />
          </Section>
        )}

        {/* Digital Assets — by category, NO secrets */}
        <Section icon={Lock} title={t("ice_assets_title")} testid="ice-assets">
          {Object.keys(data.digital_assets_by_category || {}).length === 0 ? (
            <p className="text-faded">{t("ice_no_assets")}</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(data.digital_assets_by_category).map(([cat, items]) => {
                const meta = CAT_META[cat] || { icon: Lock, key_label: "ice_cat_other" };
                const Icon = meta.icon;
                return (
                  <div key={cat}>
                    <div className="font-label text-gold flex items-center gap-2 mb-1.5">
                      <Icon className="w-3.5 h-3.5" /> {t(meta.key_label)}
                    </div>
                    <ul className="space-y-1.5 ml-5 list-disc text-gold-light marker:text-gold/60">
                      {items.map((a) => (
                        <li key={a.asset_id} className="text-sm">
                          <span className="text-gold-light">{a.title}</span>
                          {a.instruction && (
                            <span className="text-muted text-xs"> — {a.instruction}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              <p className="text-[11px] text-faded mt-2 italic">{t("ice_assets_note")}</p>
            </div>
          )}
        </Section>

        {/* Vault */}
        <Section icon={Lock} title={t("ice_vault_title")} testid="ice-vault">
          {data.vault_titles?.length === 0 ? (
            <p className="text-faded">{t("ice_no_vault")}</p>
          ) : (
            <>
              <ul className="space-y-1.5 ml-5 list-disc text-gold-light marker:text-gold/60">
                {data.vault_titles.map((v) => (
                  <li key={v.item_id} className="text-sm">{v.title}</li>
                ))}
              </ul>
              <p className="text-[11px] text-faded mt-2 italic">{t("ice_vault_note")}</p>
            </>
          )}
        </Section>

        {/* Valuables / jewelry / collectibles */}
        {_has(data.profile?.valuables) && (
          <Section icon={Gem} title={t("ice_sec_valuables")} testid="ice-valuables-summary">
            <RowList
              rows={data.profile.valuables}
              fields={[
                { key: "description", label: t("ice_v_description") },
                { key: "estimated_value", label: t("ice_v_estimated_value") },
                { key: "location", label: t("ice_v_location") },
              ]}
            />
          </Section>
        )}

        {/* Credentials location — NO passwords, only WHERE they live */}
        {_has(data.profile?.credentials_location) && (
          <Section icon={KeyRound} title={t("ice_sec_credentials")} testid="ice-credentials-summary">
            <KV
              label={t("ice_cred_type")}
              value={
                {
                  password_manager: t("ice_cred_type_pm"),
                  browser: t("ice_cred_type_browser"),
                  paper: t("ice_cred_type_paper"),
                  memory: t("ice_cred_type_memory"),
                  mixed: t("ice_cred_type_mixed"),
                }[data.profile.credentials_location.type] || null
              }
            />
            <KV label={t("ice_cred_where")} value={data.profile.credentials_location.where} />
            <KV label={t("ice_cred_how_to_access")} value={data.profile.credentials_location.how_to_access} />
            <p className="text-[11px] text-faded italic mt-2">{t("ice_b_no_creds")}</p>
          </Section>
        )}

        {/* Social media references */}
        {_has(data.profile?.social_accounts) && (
          <Section icon={Share2} title={t("ice_sec_social")} testid="ice-social-summary">
            <RowList
              rows={data.profile.social_accounts}
              fields={[
                { key: "platform", label: t("ice_soc_platform") },
                { key: "username", label: t("ice_soc_username") },
              ]}
            />
          </Section>
        )}

        {/* Funeral wishes */}
        {_has(data.profile?.funeral_wishes) && (
          <Section icon={Flower} title={t("ice_sec_funeral")} testid="ice-funeral-summary">
            <div className="text-gold-light whitespace-pre-wrap text-sm">{data.profile.funeral_wishes}</div>
            <p className="text-[11px] text-amber-300/80 italic mt-3">{t("ice_funeral_priority")}</p>
          </Section>
        )}

        {/* Additional notes */}
        {_has(data.profile?.additional_notes) && (
          <Section icon={PencilLine} title={t("ice_sec_notes")} testid="ice-notes-summary">
            <div className="text-gold-light whitespace-pre-wrap text-sm">{data.profile.additional_notes}</div>
          </Section>
        )}

        {/* Executor checklist — static guidance for whoever reads this doc.
            Renders even with no user data. Three phases: immediate / 1st week / 1st month. */}
        <Section icon={Scroll} title={t("ice_checklist_title")} testid="ice-checklist">
          <p className="text-xs text-muted mb-3">{t("ice_checklist_intro")}</p>
          {[
            { phase: "immediate", items: ["c_imm_1", "c_imm_2", "c_imm_3", "c_imm_4"] },
            { phase: "week",      items: ["c_wk_1", "c_wk_2", "c_wk_3", "c_wk_4"] },
            { phase: "month",     items: ["c_mo_1", "c_mo_2", "c_mo_3", "c_mo_4"] },
          ].map((block) => (
            <div key={block.phase} className="mb-4">
              <div className="font-display text-gold text-sm mb-2">
                {t(`ice_checklist_${block.phase}`)}
              </div>
              <ul className="space-y-1.5">
                {block.items.map((k) => (
                  <li
                    key={k}
                    className="flex items-start gap-2 text-sm text-gold-light"
                    data-testid={`ice-checklist-${k}`}
                  >
                    <span className="mt-0.5 inline-block w-3.5 h-3.5 rounded-sm border border-gold/70 shrink-0" aria-hidden />
                    <span>{t(`ice_checklist_${k}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="text-[11px] text-faded italic mt-2">{t("ice_checklist_footer")}</p>
        </Section>
      </div>
      </>
      )}

      {/* Print styles — only on this page */}
      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { background: white !important; color: black !important; }
          .ice-doc { color: black !important; }
          .ice-doc * { color: black !important; border-color: #ccc !important; }
          .ice-doc .glass-card { background: white !important; border: 1px solid #ccc !important; backdrop-filter: none !important; }
          aside, header, .print\\:hidden { display: none !important; }
        }
      `}</style>

      {/* Share modal — opens after a successful POST /ice/share */}
      {shareOpen && shareData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 print:hidden"
          role="dialog"
          aria-modal="true"
          data-testid="ice-share-modal"
        >
          <div className="glass-card rounded-md p-6 max-w-md w-full relative">
            <button
              type="button"
              onClick={() => setShareOpen(false)}
              className="absolute top-3 right-3 text-faded hover:text-gold-light transition"
              aria-label="close"
              data-testid="ice-share-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <Send className="w-5 h-5 text-gold" />
              <h2 className="font-display text-xl text-gold-light">
                {t("ice_share_modal_title")}
              </h2>
            </div>
            <p className="text-sm text-muted leading-relaxed mb-4">
              {t("ice_share_modal_intro").replace("{email}", shareData.executor_email)}
            </p>

            {/* The 6-digit code — the OWNER must share this through a separate channel */}
            <div className="bg-gold/5 border border-gold-subtle rounded-md p-4 mb-4">
              <div className="font-label text-faded text-[11px] mb-2">
                {t("ice_share_code_label")}
              </div>
              <div className="flex items-center gap-3">
                <div
                  className="font-mono text-3xl text-gold-light tracking-[0.4em] flex-1"
                  data-testid="ice-share-code"
                >
                  {shareData.code}
                </div>
                <button
                  type="button"
                  onClick={copyCode}
                  className="text-xs px-3 py-2 rounded-md border border-gold-subtle text-gold-light hover:bg-gold/10 transition flex items-center gap-1.5 shrink-0"
                  data-testid="ice-share-copy"
                >
                  {codeCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {codeCopied ? t("copied") : t("copy")}
                </button>
              </div>
              <p className="text-[11px] text-amber-300/90 mt-3 leading-relaxed">
                {t("ice_share_code_warning")}
              </p>
            </div>

            <div className="text-xs text-muted space-y-1.5 mb-4">
              <div>
                <span className="font-label text-faded">{t("ice_share_modal_link")}: </span>
                <a
                  href={shareData.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold underline break-all"
                  data-testid="ice-share-link"
                >
                  {shareData.link}
                </a>
              </div>
              <div>
                <span className="font-label text-faded">{t("ice_share_modal_expires")}: </span>
                <span className="text-gold-light">7d</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShareOpen(false)}
              className="btn-gold w-full justify-center"
              data-testid="ice-share-done"
            >
              {t("ice_share_done")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
